/*
 * Turns whatever a visitor types for their dog's breed into one canonical
 * value, so "French Bulldog", "frenchie" and "Frenchy" all become "Frenchie".
 *
 * Canonical values are the breed collection titles on the store. Anything that
 * cannot be matched with confidence becomes OTHER; the popup still sends the
 * raw text in a second property so unmatched breeds stay visible.
 *
 * Works as a plain <script> (window.DawoofieBreed) and under Node (tests).
 */
(function (root) {
  'use strict';

  var OTHER = 'Other';

  // alias lists are written already normalized: lowercase, single spaces.
  // "anywhere" aliases also match as whole words inside a longer answer
  // ("my frenchie max"). "exact" aliases are too short or too ambiguous for
  // that and only match when they are the whole answer.
  var BREEDS = [
    { name: 'Beagle', anywhere: ['beagle'], exact: [] },
    { name: 'Boston Terrier', anywhere: ['boston terrier', 'boston terrior', 'bostie'], exact: ['boston'] },
    { name: 'Boxer', anywhere: ['boxer'], exact: [] },
    { name: 'Chihuahua', anywhere: ['chihuahua', 'chiwawa', 'chihuaha'], exact: ['chi'] },
    { name: 'Corgi', anywhere: ['corgi', 'corgie', 'pembroke', 'welsh corgi'], exact: [] },
    { name: 'Dachshund', anywhere: ['dachshund', 'daschund', 'dachsund', 'doxie', 'doxin', 'dotson', 'wiener dog', 'weiner dog', 'weenie dog', 'sausage dog'], exact: ['wiener', 'weiner'] },
    { name: 'Doberman', anywhere: ['doberman', 'dobermann', 'doberman pinscher', 'dobie'], exact: [] },
    { name: 'English Bulldog', anywhere: ['english bulldog', 'british bulldog', 'english bully'], exact: ['bulldog'] },
    { name: 'Frenchie', anywhere: ['frenchie', 'frenchy', 'french bulldog', 'french bull dog', 'french bully', 'frenchi', 'bouledogue francais'], exact: [] },
    { name: 'German Shepherd Dog', anywhere: ['german shepherd', 'german shepard', 'german sheperd', 'german shephard', 'alsatian', 'gsd'], exact: [] },
    { name: 'German Shorthaired Pointer', anywhere: ['german shorthaired pointer', 'german shorthair pointer', 'german short haired pointer', 'german shorthair', 'german pointer', 'gsp'], exact: [] },
    { name: 'Golden Retriever', anywhere: ['golden retriever', 'golden retreiver', 'golden retriver'], exact: ['golden', 'goldie'] },
    { name: 'Great Dane', anywhere: ['great dane'], exact: ['dane'] },
    { name: 'Husky', anywhere: ['husky', 'huskie', 'siberian husky'], exact: [] },
    { name: 'Labrador Retriever', anywhere: ['labrador retriever', 'labrador', 'black lab', 'yellow lab', 'chocolate lab', 'choc lab'], exact: ['lab'] },
    { name: 'Pitbull', anywhere: ['pitbull', 'pit bull', 'pittie', 'pitty', 'pibble', 'american pit bull terrier', 'apbt'], exact: ['pit'] },
    { name: 'Pomeranian', anywhere: ['pomeranian', 'pomeranean'], exact: ['pom'] },
    { name: 'Pug', anywhere: ['pug'], exact: [] },
    { name: 'Rottweiler', anywhere: ['rottweiler', 'rotweiler', 'rottweiller', 'rottie'], exact: ['rott'] },
    { name: 'Shih Tzu', anywhere: ['shih tzu', 'shihtzu', 'shitzu', 'shi tzu', 'shih tsu'], exact: [] },
    { name: 'Yorkshire Terrier', anywhere: ['yorkshire terrier', 'yorkie', 'yorky', 'yorkshire'], exact: [] }
  ];

  // Crosses and breeds we do not carry that contain one of our breed words.
  // Checked first so "labradoodle" or "australian shepherd" never land on a
  // carried breed.
  var NOT_OURS = ['doodle', 'poo', 'australian shepherd', 'aussie', 'american bulldog', 'bull terrier', 'staffordshire', 'staffy', 'puggle', 'pomsky', 'chiweenie', 'malamute', 'belgian'];

  function clean(text) {
    return String(text == null ? '' : text)
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function hasPhrase(haystack, phrase) {
    return (' ' + haystack + ' ').indexOf(' ' + phrase + ' ') !== -1;
  }

  function distance(a, b) {
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      }
      prev = cur;
    }
    return prev[b.length];
  }

  function lookup(text) {
    var i, j, breed, alias, best = null, bestLen = 0;
    for (i = 0; i < BREEDS.length; i++) {
      breed = BREEDS[i];
      if (breed.exact.indexOf(text) !== -1) return breed.name;
      // the longest alias found wins, so "french bulldog" beats "bulldog"
      for (j = 0; j < breed.anywhere.length; j++) {
        alias = breed.anywhere[j];
        if (alias.length > bestLen && hasPhrase(text, alias)) {
          best = breed.name;
          bestLen = alias.length;
        }
      }
    }
    return best;
  }

  // "frenchies", "pugs", "golden retrievers"
  function singular(text) {
    return text.split(' ').map(function (word) {
      return word.length > 3 && word.charAt(word.length - 1) === 's' ? word.slice(0, -1) : word;
    }).join(' ');
  }

  function normalize(input) {
    var text = clean(input), i, j, breed, aliases, alias;
    if (!text) return '';

    for (i = 0; i < NOT_OURS.length; i++) {
      if (text.indexOf(NOT_OURS[i]) !== -1) return OTHER;
    }

    var found = lookup(text) || lookup(singular(text));
    if (found) return found;

    // Typos: compare the whole answer with each alias of 5+ letters and accept
    // one wrong letter (two for long names), but only if a single breed fits.
    var hit = null, hits = 0;
    for (i = 0; i < BREEDS.length; i++) {
      breed = BREEDS[i];
      aliases = breed.anywhere.concat(breed.exact);
      for (j = 0; j < aliases.length; j++) {
        alias = aliases[j];
        if (alias.length < 5) continue;
        if (distance(text, alias) <= (alias.length >= 9 ? 2 : 1)) {
          if (hit !== breed.name) hits++;
          hit = breed.name;
          break;
        }
      }
    }
    return hits === 1 ? hit : OTHER;
  }

  var api = { normalize: normalize, OTHER: OTHER, names: BREEDS.map(function (b) { return b.name; }) };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.DawoofieBreed = api;
})(typeof window !== 'undefined' ? window : this);
